import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import RichTextEditor from "@/components/RichTextEditor";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const STATUSES = ["Active", "Resigned", "Deceased"];

const empty = {
  name: "",
  designation: "",
  din: "",
  pan: "",
  aadhaar: "",
  mobile: "",
  email: "",
  address1: "",
  address2: "",
  city: "",
  pincode: "",
  state: "",
  country: "India",
  date_of_joining: "",
  date_of_exit: "",
  date_of_birth: "",
  status: "Active",
  password: "MIT@GSMadal",
  date_of_death: "",
  photo_url: "",
  photo_public_id: "",
  pan_url: "",
  pan_public_id: "",
  aadhaar_url: "",
  aadhaar_public_id: "",
  death_cert_url: "",
  death_cert_public_id: "",
  death_cert_resource_type: "",
  bio_html: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

const StatusPill = ({ s }) => {
  const map = {
    Active: "bg-[#3a5a40]/10 text-[#3a5a40] border-[#3a5a40]/30",
    Resigned: "bg-[#6b655b]/10 text-[#6b655b] border-[#6b655b]/30",
    Deceased: "bg-[#9e2a2b]/10 text-[#9e2a2b] border-[#9e2a2b]/30",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${map[s] || ""}`}>
      {s || "—"}
    </span>
  );
};

export default function PersonsPage({ bodyType, title, subtitle, intro, prefix, showPassword = false }) {
  const [items, setItems] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    const [a, b] = await Promise.all([
      api.get(`/bodies/${bodyType}`),
      api.get(`/masters/designation`),
    ]);
    setItems(a.data);
    setDesignations(b.data);
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyType]);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...empty,
      ...p,
      date_of_joining: p.date_of_joining || "",
      date_of_exit: p.date_of_exit || "",
      date_of_birth: p.date_of_birth || "",
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.status === "Deceased" && !form.date_of_death) {
      toast.error("Date of death is required when status is Deceased");
      return;
    }
    if (form.status === "Deceased" && !form.death_cert_url) {
      toast.error("Death certificate is required when status is Deceased");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      date_of_joining: form.date_of_joining || null,
      date_of_exit: form.date_of_exit || null,
      date_of_birth: form.date_of_birth || null,
      date_of_death: form.date_of_death || null,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/bodies/${bodyType}/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Member updated");
      } else {
        const { data } = await api.post(`/bodies/${bodyType}`, payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Member added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const p = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/bodies/${bodyType}/${p.id}`);
      setItems((arr) => arr.filter((x) => x.id !== p.id));
      toast.success("Member removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid={`${prefix}-page`}>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">{subtitle}</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">{title}</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">{intro}</p>
        </div>
        <button
          onClick={openCreate}
          data-testid={`${prefix}-add-button`}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors"
        >
          <Plus size={14} strokeWidth={1.5} />
          Add Member
        </button>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid={`${prefix}-empty`}
        >
          <Users size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">No members on record</div>
          <div className="mt-2 text-sm text-[#6b655b]">Start by adding the first member.</div>
          <button
            onClick={openCreate}
            data-testid={`${prefix}-empty-add-button`}
            className="mt-6 inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add the first member
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${prefix}-table`}>
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {["", "Name", "Designation", "Mobile", "Documents", "Joined", "Status", ""].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  data-testid={`${prefix}-row-${p.id}`}
                >
                  <td className="px-4 py-4">
                    {p.photo_url ? (
                      <img
                        src={p.photo_url}
                        alt={p.name}
                        className="w-9 h-9 rounded-full object-cover border border-[#e5e0d8]"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#f9f8f6] border border-[#e5e0d8] flex items-center justify-center font-serif text-[#234333] text-sm">
                        {(p.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-[#1c1a17]">{p.name}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{p.designation || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{p.mobile || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">
                    <div className="flex gap-2 text-[11px]">
                      {p.pan_url ? (
                        <a
                          href={p.pan_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-0.5 border border-[#234333]/30 text-[#234333] rounded-sm hover:bg-[#efece6]"
                        >
                          PAN
                        </a>
                      ) : (
                        <span className="text-[#6b655b]/50">PAN</span>
                      )}
                      {p.aadhaar_url ? (
                        <a
                          href={p.aadhaar_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-0.5 border border-[#234333]/30 text-[#234333] rounded-sm hover:bg-[#efece6]"
                        >
                          AADHAAR
                        </a>
                      ) : (
                        <span className="text-[#6b655b]/50">Aadhaar</span>
                      )}
                      {p.death_cert_url && (
                        <a
                          href={p.death_cert_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-0.5 border border-[#9e2a2b]/40 text-[#9e2a2b] rounded-sm hover:bg-[#9e2a2b]/5"
                        >
                          DEATH CERT
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-[#6b655b]">{p.date_of_joining || "—"}</td>
                  <td className="px-4 py-4">
                    <StatusPill s={p.status} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`${prefix}-edit-${p.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} /> Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete(p)}
                      data-testid={`${prefix}-delete-${p.id}`}
                      className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                    >
                      <Trash2 size={14} strokeWidth={1.5} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid={`${prefix}-dialog`}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? `Edit ${title.replace(/s$/, "")}` : `Add to ${title}`}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Full Name *">
              <input
                data-testid={`${prefix}-name-input`}
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Designation">
              <select
                data-testid={`${prefix}-designation-select`}
                className={inputCls}
                value={form.designation}
                onChange={(e) => set("designation", e.target.value)}
              >
                <option value="">— Select —</option>
                {designations.map((d) => (
                  <option key={d.id} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="DIN (if applicable)">
              <input
                data-testid={`${prefix}-din-input`}
                className={inputCls}
                value={form.din}
                onChange={(e) => set("din", e.target.value)}
              />
            </Field>
            <Field label="PAN">
              <input
                data-testid={`${prefix}-pan-input`}
                className={inputCls}
                value={form.pan}
                onChange={(e) => set("pan", e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="Aadhaar">
              <input
                data-testid={`${prefix}-aadhaar-input`}
                className={inputCls}
                value={form.aadhaar}
                onChange={(e) => set("aadhaar", e.target.value)}
              />
            </Field>
            <Field label="Mobile Number">
              <input
                data-testid={`${prefix}-mobile-input`}
                className={inputCls}
                value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)}
              />
            </Field>
            <Field label="Email">
              <input
                data-testid={`${prefix}-email-input`}
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field label="Status">
              <select
                data-testid={`${prefix}-status-select`}
                className={inputCls}
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Address Line 1" full>
              <input
                data-testid={`${prefix}-address1-input`}
                className={inputCls}
                value={form.address1}
                onChange={(e) => set("address1", e.target.value)}
              />
            </Field>
            <Field label="Address Line 2" full>
              <input
                data-testid={`${prefix}-address2-input`}
                className={inputCls}
                value={form.address2}
                onChange={(e) => set("address2", e.target.value)}
              />
            </Field>
            <Field label="City">
              <input
                data-testid={`${prefix}-city-input`}
                className={inputCls}
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
              />
            </Field>
            <Field label="PIN Code">
              <input
                data-testid={`${prefix}-pincode-input`}
                className={inputCls}
                value={form.pincode}
                onChange={(e) => set("pincode", e.target.value)}
              />
            </Field>
            <Field label="State">
              <input
                data-testid={`${prefix}-state-input`}
                className={inputCls}
                value={form.state}
                onChange={(e) => set("state", e.target.value)}
              />
            </Field>
            <Field label="Country">
              <input
                data-testid={`${prefix}-country-input`}
                className={inputCls}
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
              />
            </Field>
            <Field label="Date of Birth">
              <input
                data-testid={`${prefix}-dob-input`}
                type="date"
                className={inputCls}
                value={form.date_of_birth}
                onChange={(e) => set("date_of_birth", e.target.value)}
              />
              <div className="text-[11px] text-[#6b655b] mt-1">
                Used to order members when two or more share the same designation (elder first).
              </div>
            </Field>
            <Field label="Date of Joining">
              <input
                data-testid={`${prefix}-doj-input`}
                type="date"
                className={inputCls}
                value={form.date_of_joining}
                onChange={(e) => set("date_of_joining", e.target.value)}
              />
            </Field>
            <Field label="Date of Exit">
              <input
                data-testid={`${prefix}-doe-input`}
                type="date"
                className={inputCls}
                value={form.date_of_exit}
                onChange={(e) => set("date_of_exit", e.target.value)}
              />
            </Field>

            {form.status === "Deceased" && (
              <>
                <Field label="Date of Death *">
                  <input
                    data-testid={`${prefix}-dod-input`}
                    type="date"
                    className={inputCls}
                    value={form.date_of_death}
                    onChange={(e) => set("date_of_death", e.target.value)}
                  />
                </Field>
                <div className="md:col-span-2">
                  <FileUploader
                    kind="death_certificate"
                    label="Death Certificate *"
                    url={form.death_cert_url}
                    testid={`${prefix}-death-cert`}
                    onChange={({ url, public_id, resource_type }) =>
                      setForm((f) => ({
                        ...f,
                        death_cert_url: url,
                        death_cert_public_id: public_id,
                        death_cert_resource_type: resource_type || "",
                      }))
                    }
                  />
                  <div className="mt-2 text-[11px] text-[#9e2a2b]">
                    Required when status is Deceased. Accepts PDF or PNG, max 1 MB.
                  </div>
                </div>
              </>
            )}

            {showPassword && (
              <Field label="Member Portal Password" full>
                <div className="flex gap-2">
                  <input
                    data-testid={`${prefix}-password-input`}
                    type="text"
                    className={inputCls + " font-mono"}
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                  />
                  <button
                    type="button"
                    data-testid={`${prefix}-password-copy`}
                    onClick={() => {
                      navigator.clipboard?.writeText(form.password || "");
                      toast.success("Password copied");
                    }}
                    className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-4 py-2 rounded-sm whitespace-nowrap"
                  >
                    Copy
                  </button>
                </div>
                <div className="text-[11px] text-[#6b655b] mt-1">
                  Default <span className="font-mono">MIT@GSMadal</span> — share with the member for portal access.
                </div>
              </Field>
            )}

            <div className="md:col-span-2 pt-2">
              <div className="serif-rule mb-5" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-4">
                Bio / Notes
              </div>
              <RichTextEditor
                value={form.bio_html}
                onChange={(html) => set("bio_html", html)}
                placeholder="Brief profile, achievements, contributions, role within the trust…"
                testid={`${prefix}-bio`}
              />
            </div>

            <div className="md:col-span-2 pt-2">
              <div className="serif-rule mb-5" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-4">
                Documents & Identity
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <FileUploader
                  kind="photo"
                  label="Photo"
                  url={form.photo_url}
                  testid={`${prefix}-photo`}
                  onChange={({ url, public_id }) =>
                    setForm((f) => ({ ...f, photo_url: url, photo_public_id: public_id }))
                  }
                />
                <FileUploader
                  kind="pan"
                  label="PAN Card"
                  url={form.pan_url}
                  testid={`${prefix}-pan-doc`}
                  onChange={({ url, public_id }) =>
                    setForm((f) => ({ ...f, pan_url: url, pan_public_id: public_id }))
                  }
                />
                <FileUploader
                  kind="aadhaar"
                  label="Aadhaar Card"
                  url={form.aadhaar_url}
                  testid={`${prefix}-aadhaar-doc`}
                  onChange={({ url, public_id }) =>
                    setForm((f) => ({ ...f, aadhaar_url: url, aadhaar_public_id: public_id }))
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid={`${prefix}-cancel-button`}
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid={`${prefix}-save-button`}
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this member?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name} will be permanently removed from {title}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={`${prefix}-delete-cancel`}
              className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid={`${prefix}-delete-confirm`}
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
